import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { authErrorMessage, useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink, useGo } from '../components/Nav';
import { GoogleIcon } from '../components/icons';
import { LegalNotice } from '../components/LegalNotice';
import { Banner, Button, Input, Spinner } from '../components/ui';
import { firebaseConfigured } from '../lib/env';
import { PASSWORD_MIN, passwordProblem } from '../lib/password';

/**
 * /login and /signup. Same frame, different verbs.
 *
 * Signup is open: individuals are free forever and org members join through
 * the same door. There is no "request a demo" step and there should never be
 * one.
 */

export function AuthFrame({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="flex min-h-full flex-col bg-slate-950">
      <header className="border-b border-hairline">
        <div className="mx-auto flex h-14 w-full max-w-5xl items-center px-4 sm:px-6">
          <Wordmark />
        </div>
        <ClapperRule />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <h1 className="text-h2 text-chalk">{title}</h1>
          <p className="mt-1.5 text-body text-chalk-dim">{subtitle}</p>

          {!firebaseConfigured ? (
            <div className="mt-5">
              <Banner tone="danger" title="Sign-in is not configured">
                This build has no Firebase credentials. Run <code>yarn ssm:fetch:local</code> (or
                the environment you meant) before starting the dev server.
              </Banner>
            </div>
          ) : null}

          <div className="mt-6 space-y-5">{children}</div>

          <div className="mt-6 text-caption text-chalk-faint">{footer}</div>
        </div>
      </main>
    </div>
  );
}

function GoogleButton({ label, onError }: { label: string; onError: (message: string) => void }) {
  const { signInWithGoogle } = useAuth();
  const [busy, setBusy] = useState(false);

  return (
    <Button
      variant="secondary"
      size="lg"
      className="w-full"
      busy={busy}
      disabled={!firebaseConfigured}
      onClick={() => {
        setBusy(true);
        signInWithGoogle()
          .catch((error: unknown) => onError(authErrorMessage(error)))
          .finally(() => setBusy(false));
      }}
    >
      {!busy ? <GoogleIcon size={16} /> : null}
      {label}
    </Button>
  );
}

function Divider() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-hairline" />
      <span className="text-micro uppercase tracking-[0.1em] text-chalk-faint">or</span>
      <div className="h-px flex-1 bg-hairline" />
    </div>
  );
}

/**
 * Where to go once signed in: `?next=` if it is a path on this site, the
 * library otherwise. A full URL or a protocol-relative one is ignored, so a
 * login link cannot be used to bounce someone off to another domain.
 */
function nextPath(): string {
  const next = new URLSearchParams(window.location.search).get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

/** Both pages bounce onward the moment a session exists. */
function useRedirectWhenSignedIn() {
  const { user, loading } = useAuth();
  const go = useGo();

  useEffect(() => {
    if (loading || !user) return;
    // A brand-new account is sent to the welcome steps by the app itself,
    // from the account row, so it happens whichever page the sign-in was on.
    go(nextPath(), { replace: true });
  }, [go, loading, user]);

  return loading;
}

/** The other page, keeping `next` so a detour through signup still lands. */
function otherPage(path: '/login' | '/signup'): string {
  const next = nextPath();
  return next === '/' ? path : `${path}?next=${encodeURIComponent(next)}`;
}

export function LoginPage() {
  const { signInWithEmail } = useAuth();
  const restoring = useRedirectWhenSignedIn();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    signInWithEmail(email, password)
      .catch((cause: unknown) => setError(authErrorMessage(cause)))
      .finally(() => setBusy(false));
  };

  // A page of its own rather than an action on this one: asking for a link is
  // its own errand, with its own "check your inbox" to land on. Whatever was
  // typed here goes with it, so nobody enters their address twice.
  const forgot = email.trim() ? `/forgot-password?email=${encodeURIComponent(email.trim())}` : '/forgot-password';

  if (restoring) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950">
        <Spinner size={22} className="text-chalk-faint" />
      </div>
    );
  }

  return (
    <AuthFrame
      title="Sign in"
      subtitle="Pick up where you left off."
      footer={
        <>
          New here?{' '}
          <AppLink to={otherPage('/signup')} className="text-signal hover:underline">
            Create an account
          </AppLink>
          .
        </>
      }
    >
      <GoogleButton label="Continue with Google" onError={setError} />
      <Divider />

      <form onSubmit={submit} className="space-y-4">
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />

        {error ? <Banner tone="danger">{error}</Banner> : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          busy={busy}
          disabled={!firebaseConfigured}
        >
          Sign in
        </Button>
      </form>

      <AppLink
        to={forgot}
        className="text-caption text-chalk-faint underline-offset-2 hover:text-chalk-dim hover:underline"
      >
        Forgot your password?
      </AppLink>
    </AuthFrame>
  );
}

export function SignupPage() {
  const { signUpWithEmail } = useAuth();
  const restoring = useRedirectWhenSignedIn();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const problem = passwordProblem(password, email);
    if (problem) {
      setError(problem);
      return;
    }

    setBusy(true);
    signUpWithEmail(name, email, password)
      .catch((cause: unknown) => setError(authErrorMessage(cause)))
      .finally(() => setBusy(false));
  };

  if (restoring) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950">
        <Spinner size={22} className="text-chalk-faint" />
      </div>
    );
  }

  return (
    <AuthFrame
      title="Create your account"
      subtitle="Free forever for individuals. No card, no trial clock."
      footer={
        <>
          Already have an account?{' '}
          <AppLink to={otherPage('/login')} className="text-signal hover:underline">
            Sign in
          </AppLink>
          .
        </>
      }
    >
      <GoogleButton label="Continue with Google" onError={setError} />
      <Divider />

      <form onSubmit={submit} className="space-y-4">
        <Input
          label="Your name"
          autoComplete="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint="Shown on the videos you share."
          required
        />
        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint={`At least ${PASSWORD_MIN} characters. A phrase works well.`}
          minLength={PASSWORD_MIN}
          required
        />

        {error ? <Banner tone="danger">{error}</Banner> : null}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          busy={busy}
          disabled={!firebaseConfigured}
        >
          Create account
        </Button>
        <LegalNotice />
      </form>
    </AuthFrame>
  );
}
